import React, {useState} from 'react';
import {createFragmentContainer, graphql} from 'react-relay';
import {ButtonDropdownField, DropdownList, FlexRow, Modal, Text} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {AssociateBillToModal_viewer} from './__generated__/AssociateBillToModal_viewer.graphql';
import CreateBulkClientBillToMutation from '../../../../mutations/invoicing/CreateBulkClientBillToMutation';
import Util from '../../../shared/util/util';

type AssociateBillToModalProps = {
	billToId: string;
	billFromId: string;
	closeModal: () => void;
	viewer: AssociateBillToModal_viewer;
};

const AssociateBillToModal = ({billToId, billFromId, closeModal, viewer}: AssociateBillToModalProps) => {
	const {formatMessage} = useIntl();
	const [clientId, setClientId] = useState<string | undefined>(undefined);
	const [selectedClient, setSelectedClient] = useState(
		viewer.company?.clients?.edges?.find(client => client?.node?.id === clientId)
	);
	const clients =
		viewer.company?.clients?.edges
			?.map(edge => edge?.node)
			.filter(client => !client?.clientBillTos?.edges?.some(billTo => billTo?.node?.billTo?.id === billToId)) || [];
	const associateBillTo = () => {
		Util.CommitMutation(
			CreateBulkClientBillToMutation,
			{
				billTos: [{billToId: billToId, billFromId: billFromId, clientId: clientId}],
			},
			closeModal
		);
	};

	const handleSelectClient = clientId => {
		setClientId(clientId);
		setSelectedClient(viewer.company?.clients?.edges?.find(client => client?.node?.id === clientId));
	};

	const areThereClients = clients.length > 0;

	return (
		<Modal onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'settings_finance.bill_to.associate_client'})}</Modal.Title>
			<Modal.Content>
				{areThereClients ? (
					<>
						<Text size={'1'}>
							<b>{formatMessage({id: 'settings_finance.select_client'})}</b>
						</Text>
						<Text>{formatMessage({id: 'settings_finance.associate_single_bill_to_entity'})}</Text>
						<DropdownList>
							<DropdownList.Trigger>
								<ButtonDropdownField>
									{selectedClient
										? selectedClient.node?.name
										: formatMessage({id: 'settings_finance.select_client'})}
								</ButtonDropdownField>
							</DropdownList.Trigger>
							<DropdownList.Content offset={5} align={'start'}>
								{clients.map(client => (
									<DropdownList.Item key={client?.id} onClick={() => handleSelectClient(client?.id)}>
										<FlexRow>{client?.name}</FlexRow>
									</DropdownList.Item>
								))}
							</DropdownList.Content>
						</DropdownList>
					</>
				) : (
					<Text size={'1'}>{formatMessage({id: 'settings_finance.all_clients_linked_to_bill_to_message'})}</Text>
				)}
			</Modal.Content>
			{areThereClients ? (
				<Modal.Footer>
					<Modal.PrimaryFooterButton onPress={associateBillTo} disabled={!clientId}>
						{formatMessage({id: 'common.associate'})}
					</Modal.PrimaryFooterButton>

					<Modal.SecondaryFooterButton onPress={closeModal}>
						{formatMessage({id: 'common.close'})}
					</Modal.SecondaryFooterButton>
				</Modal.Footer>
			) : (
				<Modal.Footer>
					<Modal.PrimaryFooterButton onPress={closeModal} disabled={!clientId}>
						{formatMessage({id: 'common.close'})}
					</Modal.PrimaryFooterButton>
				</Modal.Footer>
			)}
		</Modal>
	);
};

export const AssociateBillToModalQuery = graphql`
	query AssociateBillToModalQuery {
		viewer {
			actualPersonId
			component(name: "AssociateBillToModal")
			...AssociateBillToModal_viewer
		}
	}
`;

export default createFragmentContainer(AssociateBillToModal, {
	viewer: graphql`
		fragment AssociateBillToModal_viewer on Viewer {
			id
			company {
				id
				clients(first: 100000) {
					edges {
						node {
							id
							name
							clientBillTos(first: 10000) @connection(key: "Client_clientBillTos", filters: []) {
								edges {
									node {
										id
										billFrom {
											id
										}
										billTo {
											id
											invoiced
										}
										clientId
									}
								}
							}
						}
					}
				}
			}
		}
	`,
});
